// SPDX-License-Identifier: AGPL-3.0-or-later

import {GREY_THEME_CSS} from '@app/features/theme/commands/GreyThemeCss';
import {TCN_THEME_CSS} from '@app/features/theme/commands/TcnThemeCss';
import ThemeLibrary, {type ThemeLibraryTheme} from '@app/features/theme/state/ThemeLibrary';
import {broadcastThemeStudioMessage} from '@app/features/theme_studio/state/ThemeStudioBroadcast';
import {type ThemeType, ThemeTypes} from '@fluxer/constants/src/UserConstants';

// UI-only presets. The API continues to receive the upstream Dark theme value.
export type ThemeChoice = ThemeType | 'tcn' | 'grey';
export const TCN_THEME_ID = 'tcn-bundled-theme-v1';
export const GREY_THEME_ID = 'tcn-grey-theme-v1';
const presets = [
	{choice: 'tcn', id: TCN_THEME_ID, css: TCN_THEME_CSS, file: 'TCN.theme.css'},
	{choice: 'grey', id: GREY_THEME_ID, css: GREY_THEME_CSS, file: 'Grey.theme.css'},
] as const;

function getPreset(theme: ThemeLibraryTheme) {
	return (
		presets.find((preset) => theme.id === preset.id) ??
		presets.find((preset) => theme.css.replace(/\r\n/g, '\n').trim() === preset.css.replace(/\r\n/g, '\n').trim())
	);
}

export function getThemeChoice(baseTheme: ThemeType): ThemeChoice {
	if (baseTheme !== ThemeTypes.DARK) return baseTheme;
	// Theme Studio can enable both; the last palette in the CSS order wins.
	const preset = ThemeLibrary.enabledThemes
		.map(getPreset)
		.filter((value) => value !== undefined)
		.at(-1);
	return preset?.choice ?? baseTheme;
}

export async function selectThemeChoice(choice: ThemeChoice): Promise<ThemeType> {
	// Read back library changes from other windows before preserving the enabled set.
	await ThemeLibrary.reload();
	const preset = presets.find((value) => value.choice === choice);
	if (!ThemeLibrary.isHydrated || ThemeLibrary.loadFailed) {
		// Base themes still work when storage is unavailable and no preset CSS is active.
		if (choice !== 'tcn' && choice !== 'grey' && !ThemeLibrary.enabledThemes.some(getPreset)) return choice;
		throw new Error('Theme library unavailable');
	}
	let themes = preset ? ThemeLibrary.themes.filter((theme) => getPreset(theme) === preset) : [];
	if (preset && themes.length === 0) {
		const theme = ThemeLibrary.createThemeFromCss(preset.css, preset.file, 'import', {id: preset.id});
		await ThemeLibrary.saveTheme(theme);
		themes = [theme];
	}
	// Reuse bundled/imported palettes, preserving edits and unrelated custom themes.
	const selected =
		themes.find((theme) => theme.id === preset?.id && ThemeLibrary.enabledThemeIds.includes(theme.id)) ??
		themes.find((theme) => ThemeLibrary.enabledThemeIds.includes(theme.id)) ??
		themes[0];
	for (const theme of ThemeLibrary.themes.filter((value) => getPreset(value) !== undefined)) {
		const enabled = theme.id === selected?.id;
		if (ThemeLibrary.enabledThemeIds.includes(theme.id) !== enabled)
			await ThemeLibrary.setThemeEnabled(theme.id, enabled);
	}
	broadcastThemeStudioMessage({type: 'themeLibrary', revision: ThemeLibrary.revision});
	return choice === 'tcn' || choice === 'grey' ? ThemeTypes.DARK : choice;
}
